<template>
  <div id="stop-overview">
    <v-tabs v-model="currentTab" show-arrows>
      <v-tab v-for="tab in tabs" :key="tab.icon">
        <v-icon class="me-3">
          {{ tab.icon }}
        </v-icon>
        <span>
          {{ tab.title }}
        </span>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="currentTab" touchless>
      <v-tab-item>
        <v-card class="mb-4">
          <v-card-text>
            <v-row>
              <v-col>
                <h2 class="text-left text-xl font-weight-semibold label">Stop name</h2>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="10">
                <v-alert border="left" color="primary" text dense>
                  <p class="text--primary font-weight-medium font-italic">
                    {{ stop.stop_name }}
                  </p>
                </v-alert>
              </v-col>
              <v-col cols="1" class="d-flex justify-center align-center">
                <v-btn class="" fab dark small color="primary" @click="TTSpreview(stop.stop_name)">
                  <v-icon x-large>
                    {{ icons.mdiPlayCircle }}
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="10">
                <v-textarea
                  v-model="stop_settings.stop_tts"
                  label="Custom stop name TTS"
                  rows="1"
                  outlined
                  hide-details
                  :rules="[v => !v || (v && v.length <= 64) || 'Stop name must be 64 characters or less']"
                >
                </v-textarea>
                <tooltip-for-text-to-speech-field></tooltip-for-text-to-speech-field>
              </v-col>
              <v-col cols="1" class="d-flex justify-center align-center">
                <v-btn class="mb-8" fab dark small color="primary" @click="TTSpreview(stop_settings.stop_tts)">
                  <v-icon x-large>
                    {{ icons.mdiPlayCircle }}
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <h2 class="text-left text-xl font-weight-semibold label">Point of interest message</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="10">
                <v-textarea
                  v-model="stop_settings.additional_text"
                  label="Point of interest message text"
                  rows="6"
                  outlined
                  hide-details="auto"
                  :rules="[
                    v => !v || (v && v.length <= 256) || 'Additional text message must be 256 characters or less',
                  ]"
                >
                </v-textarea>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="10">
                <v-textarea
                  v-model="stop_settings.additional_tts"
                  label="Point of interest message TTS"
                  rows="6"
                  outlined
                  hide-details="auto"
                  :hint="
                    stop_settings.additional_tts && !stop_settings.additional_text
                      ? 'Warning: The audio won\'t play unless the \'Additional message text\' field is also set'
                      : null
                  "
                  persistent-hint
                  :rules="[
                    v => !v || (v && v.length <= 256) || 'Additional TTS message must be 256 characters or less',
                  ]"
                >
                </v-textarea>
                <tooltip-for-text-to-speech-field></tooltip-for-text-to-speech-field>
              </v-col>
              <v-col cols="1" class="d-flex justify-center align-center">
                <v-btn class="mb-8" fab dark small color="primary" @click="TTSpreview(stop_settings.additional_tts)">
                  <v-icon x-large>
                    {{ icons.mdiPlayCircle }}
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-text>
            <v-row class="mb-4">
              <v-col>
                <h2>Point of interest additional message for specific route(s)</h2>

                <v-alert border="left" color="info" text dense class="mt-4 mb-0 pb-0">
                  <p class="text--primary font-weight-medium font-italic">
                    These settings won’t currently take effect on bus.
                  </p>
                </v-alert>
              </v-col>
            </v-row>
            <v-expansion-panels :value="openedExpansionPanelIndex" class="mb-4">
              <v-expansion-panel v-for="(group, i) in stop_settings.custom_additional_message_for_route" :key="i">
                <div class="header-wrapper d-flex" style="align-items: center">
                  <v-expansion-panel-header>
                    <h3>
                      <template v-if="group.route_ids.length">
                        Additional message for route
                        <route-badge
                          v-for="routeId in group.route_ids"
                          :key="routeId"
                          :route="getRouteById(routeId)"
                          :size="30"
                          text-class="text-xs"
                          avatar-class="vehicle-avatar"
                          class="mr-2"
                        ></route-badge>
                      </template>
                      <template v-else> New Group </template>
                    </h3>
                  </v-expansion-panel-header>

                  <v-switch
                    v-model="group.message_enabled"
                    class="v-input--reverse mt-0 pt-0 pr-4"
                    hide-details
                    label="Enable"
                    title="Enable"
                  >
                  </v-switch>
                </div>
                <v-expansion-panel-content>
                  <v-row class="align-right mb-2">
                    <v-col class="filter d-flex align-center" cols="8">
                      <v-autocomplete
                        v-model="tempGroupModel[i]"
                        label=""
                        :items="routesWithCustomAdditionalMessageItemsSelect"
                        item-value="route_id"
                        :item-text="item => item.route_short_name + ' - ' + item.route_long_name"
                        return-object
                        outlined
                        hide-details
                        placeholder="Add one or multiple routes to group"
                        multiple
                        dense
                      >
                      </v-autocomplete>
                    </v-col>
                    <v-card-text v-if="group.route_ids.length" class="px-3">
                      <v-simple-table :key="forceRefreshKey">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">ID</th>
                              <th class="text-left">Type</th>
                              <th class="text-left">Description</th>
                              <th class="text-left"></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="routeId in group.route_ids"
                              :key="routeId"
                              :set="(route = routes.find(r => r.route_id === parseInt(routeId, 10)))"
                            >
                              <td>
                                <route-badge
                                  :key="routeId"
                                  :route="route"
                                  :size="38"
                                  text-class="text-xs"
                                  avatar-class="vehicle-avatar"
                                  class="mr-2"
                                ></route-badge>
                              </td>
                              <td>
                                <v-tooltip bottom class="text--primary">
                                  <template #activator="{ on, attrs }">
                                    <v-icon size="20" v-bind="attrs" v-on="on">
                                      {{ getRouteTypeIcon(route.route_type) }}
                                    </v-icon>
                                  </template>
                                  <span>{{ getRouteTypeLabel(route.route_type) }}</span>
                                </v-tooltip>
                              </td>
                              <td>{{ route.route_long_name }}</td>
                              <td>
                                <v-tooltip bottom>
                                  <template #activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      small
                                      v-bind="attrs"
                                      color="primary"
                                      v-on="on"
                                      @click="removeRouteFromGroup(i, route.route_id)"
                                    >
                                      <v-icon size="18">
                                        {{ icons.mdiLinkOff }}
                                      </v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Remove route {{ route.route_id }} from transfer routes</span>
                                </v-tooltip>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card-text>

                    <v-col class="d-flex justify-center align-center" cols="3">
                      <v-btn small class="no-text-transform" color="primary" @click="addRouteToGroup(i)">
                        Add to selection
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="10">
                      <v-textarea
                        v-model="group.custom_additional_message"
                        label="Additional message text"
                        rows="6"
                        outlined
                        hide-details="auto"
                        :rules="[
                          v =>
                            !v ||
                            (v && v.length <= 256) ||
                            'Custom additional text message must be 256 characters or less',
                        ]"
                      >
                      </v-textarea>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="10">
                      <v-textarea
                        v-model="group.custom_additional_message_tts"
                        label="Additional message TTS"
                        rows="6"
                        outlined
                        hide-details="auto"
                        :rules="[
                          v =>
                            !v ||
                            (v && v.length <= 256) ||
                            'Custom additional TTS message must be 256 characters or less',
                        ]"
                      >
                      </v-textarea>
                      <tooltip-for-text-to-speech-field></tooltip-for-text-to-speech-field>
                    </v-col>
                    <v-col cols="1" class="d-flex justify-center align-center">
                      <v-btn
                        class="mb-8"
                        fab
                        dark
                        small
                        color="primary"
                        @click="TTSpreview(group.custom_additional_message_tts)"
                      >
                        <v-icon x-large>
                          {{ icons.mdiPlayCircle }}
                        </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-row>
              <v-col class="d-flex justify-center">
                <v-btn large color="primary" @click="addNewCustomAdditionalMessageGroup">Add new group</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card class="mb-4 pb-3">
          <v-card-text>
            <v-row>
              <v-col>
                <v-switch
                  v-model="stop_settings.transfers_enabled"
                  label="Enable transfers annoucement"
                  class="v-input--reverse"
                  hide-details
                >
                </v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <div v-if="stop_settings.transfers_enabled">
          <v-card class="mb-4">
            <v-card-text>
              <v-row>
                <v-col>
                  <h2 class="text-left text-xl font-weight-semibold label">Custom transfer announcement</h2>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="10">
                  <v-textarea
                    v-model="stop_settings.custom_transfer_tts"
                    label="Custom transfer message TTS"
                    rows="2"
                    outlined
                    hint="(This will take precedence over any route transfers below)"
                    persistent-hint
                    :rules="[
                      v => !v || (v && v.length <= 256) || 'Custom transfer message must be 256 characters or less',
                    ]"
                  >
                  </v-textarea>
                  <tooltip-for-text-to-speech-field></tooltip-for-text-to-speech-field>
                </v-col>
                <v-col cols="1" class="d-flex justify-center align-center">
                  <v-btn
                    class="mb-8"
                    fab
                    dark
                    small
                    color="primary"
                    @click="TTSpreview(stop_settings.custom_transfer_tts)"
                  >
                    <v-icon x-large>
                      {{ icons.mdiPlayCircle }}
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-if="stop_settings.transfers_enabled">
            <v-card-text>
              <v-row>
                <v-col>
                  <h2 class="text-left text-xl font-weight-semibold label">Route transfers</h2>
                </v-col>
              </v-row>
              <v-row class="align-right">
                <v-col class="filter d-flex align-center" cols="9">
                  <v-autocomplete
                    v-model="selectedRoutes"
                    label=""
                    :items="addToTransferCandidates"
                    item-value="route_id"
                    :item-text="item => item.route_short_name + ' - ' + item.route_long_name"
                    return-object
                    outlined
                    hide-details
                    placeholder="Add one or multiple routes to transfer list"
                    multiple
                    dense
                  >
                  </v-autocomplete>
                </v-col>
                <v-col class="d-flex justify-center align-center" cols="3">
                  <v-btn small class="no-text-transform" color="primary" @click="addToTransferList">
                    Add to transfer list
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-text v-if="stop_settings.transfers_enabled">
              <v-data-table
                :headers="transfersTableHeaders"
                :items="transferRows"
                :single-expand="false"
                item-key="route_id"
                hide-default-footer
                hide-default-header
                disable-pagination
              >
                <template #[`item.route_id`]="{ item }">
                  <h2 class="text-sm font-weight-bold mb-1 mt-1">
                    <route-badge
                      :route="item.route"
                      :size="38"
                      :avatar-class="item.isDeleted ? 'vehicle-avatar opacity-60' : 'vehicle-avatar'"
                      text-class="text-1xl"
                    ></route-badge>
                  </h2>
                </template>

                <template #[`item.route_type`]="{ item }">
                  <div>
                    <span class="text-capitalize font-weight-semibold text--primary">
                      <v-tooltip bottom class="text--primary">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            size="20"
                            :class="item.isDeleted ? 'opacity-60' : ''"
                            v-bind="attrs"
                            v-on="on"
                          >
                            {{ getTransferRouteTypePresentation(item).icon }}
                          </v-icon>
                        </template>
                        <span>{{ getTransferRouteTypePresentation(item).text }}{{ item.isDeleted ? " [DELETED]" : "" }}</span>
                      </v-tooltip>
                    </span>
                  </div>
                </template>

                <template #[`item.route_long_name`]="{ item }">
                  <span
                    class="text-capitalize font-weight-semibold text--primary"
                    :class="item.isDeleted ? 'opacity-60' : ''"
                  >
                    {{ item.route.route_long_name }}{{ item.isDeleted ? " [DELETED]" : "" }}
                  </span>
                </template>

                <template #[`item.remove`]="{ item }">
                  <div class="d-flex align-center justify-right">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          color="primary"
                          v-on="on"
                          @click="removeFromTransferList(item)"
                        >
                          <v-icon size="18">
                            {{ icons.mdiLinkOff }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>Remove route {{ item.route_id }} from transfer routes</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </div>
        <div v-else>
          <v-row class="mb-4">
            <v-col class="d-flex justify-center align-center" cols="12">
              <h2 class="text-left text-xl font-weight-semibold label">Transfer disabled</h2>
            </v-col>
          </v-row>
        </div>
      </v-tab-item>

      <v-tab-item v-if="canNotes">
        <v-card>
          <!-- Notes Filter fields start -->
          <v-row class="pt-4 pb-0 ma-0">
            <v-col cols="12" sm="4">
              <v-select
                v-model="sqPriority"
                :items="priorityItems"
                item-text="text"
                item-value="value"
                label="Priority"
                placeholder="Priority"
                outlined
                hide-details
                clearable
                dense
                class="user-search me-3"
                @change="val => addToSearchHistory(val, 'sqPriority')"
              >
              </v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="sqClaimant"
                label="Created By"
                outlined
                hide-details
                clearable
                persistent-clear
                dense
                class="user-search me-3"
                @change="val => addToSearchHistory(val, 'sqClaimant')"
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-menu
                v-model="menu"
                :close-on-content-click="true"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="sqCreated"
                    label="Date"
                    readonly
                    v-bind="attrs"
                    outlined
                    hide-details
                    dense
                    clearable
                    class="user-search me-3"
                    v-on="on"
                    @change="val => addToSearchHistory(val, 'sqCreated')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="sqCreated" no-title @input="menu = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <!-- Notes Filter fields end -->

          <!-- Notes Table start -->
          <v-data-table
            :headers="noteHeaders"
            :items="filteredNotes"
            :sort-by.sync="noteSortBy"
            :sort-desc.sync="noteSortDesc"
            :custom-sort="customSort"
            class="font-weight-semibold text--primary"
            :must-sort="true"
          >
            <template v-slot:item.id="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.id }}</span>
                  </div>
                </div>
              </router-link>
            </template>

            <template v-slot:item.title="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.title }}</span>
                  </div>
                </div>
              </router-link>
            </template>

            <template v-slot:item.issue_type="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.issue_type }}</span>
                  </div>
                </div>
              </router-link>
            </template>

            <template v-slot:item.priority="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.priority }}</span>
                  </div>
                </div>
              </router-link>
            </template>

            <template v-slot:item.status="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.status }}</span>
                  </div>
                </div>
              </router-link>
            </template>

            <template v-slot:item.claimant="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <span class="font-weight-semibold text--primary">{{ item.claimant }}</span>
                  </div>
                </div>
              </router-link>
            </template>

            <template v-slot:item.convertCreatedDate="{ item }">
              <router-link
                :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                class="cursor-pointer text-decoration-none"
              >
                <div class="d-flex">
                  <div class="d-flex flex-column">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span class="font-weight-semibold text--primary" v-html="convertCreatedDate(item)"></span>
                  </div>
                </div>
              </router-link>
            </template>
          </v-data-table>
          <!-- Notes Table end -->

          <NotesForm :notes-data="notesData" />
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex"
import {
  mdiTransitDetour,
  mdiMessagePlus,
  mdiOpenInNew,
  mdiLinkOff,
  mdiTrainCar,
  mdiTableColumnPlusBefore,
  mdiPlayCircle,
  mdiCommentTextOutline,
  mdiTextBoxMultipleOutline,
  mdiAlert,
} from "@mdi/js"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import RouteBadge from "@/components/route/Badge.vue"
import TooltipForTextToSpeechField from "../../components/TooltipForTextToSpeechField.vue"
import NotesForm from "../../notes/components/NotesForm.vue"
import store from "@/store"
import globalHelpers from "@/mixins/globalHelpers"
import urlHelpers from "@/mixins/urlHelpers"

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: "StopOverview",
  components: {
    RouteBadge,
    TooltipForTextToSpeechField,
    NotesForm,
  },
  mixins: [globalHelpers, urlHelpers],
  data() {
    return {
      currentTab: "",
      selectedRoutes: [],
      curNotes: [],
      stop_settings: {},
      routeToCustom: null,
      transfersTableHeaders: [
        { text: "ID", value: "route_id", sortable: false },
        { text: "Type", value: "route_type", sortable: false },
        { text: "Description", value: "route_long_name", sortable: false },
        { text: "", value: "route_url", sortable: false },
        {
          text: "",
          value: "remove",
          sortable: false,
          align: "start",
        },
      ],
      icons: {
        mdiOpenInNew,
        mdiLinkOff,
        mdiTrainCar,
        mdiTableColumnPlusBefore,
        mdiPlayCircle,
        mdiCommentTextOutline,
        mdiAlert,
      },
      sqCreated: "",
      sqClaimant: "",
      sqPriority: null,
      noteSortBy: "created",
      menu: false,
      // Notes Table Headers
      noteHeaders: [
        { text: "Note ID", value: "id" },
        { text: "Title", value: "title" },
        { text: "Type", value: "issue_type" },
        { text: "Priority", value: "priority" },
        { text: "Status", value: "status" },
        { text: "Created By", value: "claimant" },
        { text: "Created", value: "convertCreatedDate" },
      ],
      priorityItems: [
        {
          text: "Low",
          value: "Low",
        },
        {
          text: "Medium",
          value: "Medium",
        },
        {
          text: "High",
          value: "High",
        },
        {
          text: "Urgent",
          value: "Urgent",
        },
      ],
      is_loading: true,
      notesData: {
        stop_id: null,
        claimant: this.$auth.user.name,
      },
      noteSortDesc: true, // true for descending order, false for ascending order
      openedExpansionPanelIndex: null,
      tempGroupModel: [],
      forceRefreshKey: 0,
    }
  },
  computed: {
    ...mapState({
      routes: state => state.gtfs.routes,
      stop: state => state.selected_stop,
      notes: state => state.gtfs.notes,
    }),

    tabs() {
      const tabs = [
        { title: "Configuration", icon: mdiMessagePlus },
        { title: "Transfers", icon: mdiTransitDetour },
      ]

      if (this.canNotes) {
        tabs.push({ title: "Notes", icon: mdiTextBoxMultipleOutline })
      }

      return tabs
    },

    addToTransferCandidates() {
      const existingTransfers = this.stop_settings.transfers.map(t => parseInt(t.route_id, 10))
      const candidates = this.routes.filter(route => !existingTransfers.includes(route.route_id))

      const heading1 = { disabled: true, divider: true, header: "Routes Going Through The Stop" }
      const group1 = candidates.filter(route => this.stop.routes.includes(route.route_id))
      const heading2 = { disabled: true, divider: true, header: "Other Routes" }
      const group2 = candidates.filter(route => !this.stop.routes.includes(route.route_id))

      return [heading1, ...group1, heading2, ...group2]
    },

    transferRows() {
      return (this.stop_settings.transfers || []).map(transfer => {
        const routeId = parseInt(transfer.route_id, 10)
        const route = this.routes.find(r => r.route_id === routeId)
        const fallbackRoute = {
          route_short_name: String(transfer.route_id),
          route_long_name: `Route ${transfer.route_id}`,
          route_color: "757575",
          route_type: transfer.route_type ?? null,
        }

        return {
          ...transfer,
          route: route || fallbackRoute,
          routeType: route ? route.route_type : fallbackRoute.route_type,
          isDeleted: !route,
        }
      })
    },

    routesWithCustomAdditionalMessageItemsSelect() {
      const unavailableRoutes =
        this.stop_settings.custom_additional_message_for_route?.map(group => group.route_ids)?.flat() || []

      // we're invoking forceRefreshKey just to force the refresh of the select field
      if (!unavailableRoutes.length && this.forceRefreshKey !== null) {
        return this.relatedRoutes
      }

      return this.relatedRoutes.filter(route => !unavailableRoutes.includes(route.route_id))
    },

    canNotes() {
      return this.$auth.hasScope("notes")
    },

    // Notes Filtering
    filteredNotes() {
      // Ensure curNotes is an array before calling filter
      if (!Array.isArray(this.curNotes)) return []

      return this.curNotes.filter(note => {
        let include = true

        if (include && this.sqCreated !== "" && this.sqCreated !== null) {
          // eslint-disable-next-line no-undef
          const date = dayjs(this.formatDate(note.created, "en-NZ"), "D/MM/YYYY, h:mm:ss a")
          include = date.format("YYYY-MM-DD") === this.sqCreated
        }

        if (include && this.sqClaimant !== "" && this.sqClaimant !== null) {
          include = note.claimant.toLowerCase().includes(this.sqClaimant.toLowerCase())
        }

        if (include && this.sqPriority !== "" && this.sqPriority !== null) {
          include = note.priority === this.sqPriority
        }

        return include
      })
    },

    convertCreatedDate() {
      // eslint-disable-next-line func-names
      return function (item) {
        return item.created ? this.formatDate(item.created, "en-NZ") : "Unknown"
      }
    },

    relatedRoutes() {
      return this.stop.routes.map(id => this.routes.find(route => route.route_id === id))
    },

    stopSettingsToSave() {
      // Ensure custom_additional_message_for_route is defined and is an array
      const customMessages = Array.isArray(this.stop_settings.custom_additional_message_for_route)
        ? this.stop_settings.custom_additional_message_for_route
        : []

      // Filter the custom messages
      const filteredMessages = customMessages.filter(
        group => group.route_ids.length && group.custom_additional_message && group.custom_additional_message_tts,
      )

      // Return the updated stop settings
      return {
        ...this.stop_settings,
        custom_additional_message_for_route: filteredMessages,
      }
    },
  },

  async created() {
    this.stop_settings = JSON.parse(JSON.stringify(this.stop.config))

    this.notesData.stop_id = this.stop.stop_id

    try {
      this.curNotes = await store.dispatch("getNotes", {
        stop_id: this.stop.stop_id,
      })
    } catch (error) {
      console.error("Failed to load notes:", error)
      this.curNotes = []
    }
  },

  methods: {
    ...mapActions(["TTSpreview"]),

    getRouteById(routeId) {
      return this.routes.find(route => route.route_id === parseInt(routeId, 10))
    },

    getRouteTypePresentation(routeType) {
      return this.routeTypePresentation(routeType)
    },

    getTransferRouteTypePresentation(transferRow) {
      if (transferRow.routeType === null || transferRow.routeType === undefined) {
        return { icon: this.icons.mdiTrainCar, text: "Other" }
      }

      return this.getRouteTypePresentation(transferRow.routeType)
    },

    addToTransferList() {
      this.selectedRoutes.forEach(route => {
        this.stop_settings.transfers.push({ route_id: route.route_id, custom_route_tts: null, is_enabled: false })
      })
      this.selectedRoutes = []

      store.commit("SET_SNACKBAR", {
        text: "The selected routes have been added to transfer route list",
      })
    },

    removeFromTransferList(route) {
      this.stop_settings.transfers.splice(
        this.stop_settings.transfers.findIndex(
          transfer => parseInt(transfer.route_id, 10) === parseInt(route.route_id, 10),
        ),
        1,
      )
    },

    saveStopConfig() {
      store.dispatch("saveStopConfig", this.stopSettingsToSave)
    },

    /**
     * Overrides default sorting by table headers.
     */
    customSort(items, sortBy, sortDesc) {
      const sortByField = sortBy[0] ?? ""
      const sortDescending = sortDesc[0] ?? false

      items.sort((a, b) => {
        let sortA = null
        let sortB = null

        // Handle sorting based on clicked header column
        switch (sortByField) {
          case "convertCreatedDate": {
            // We don't want to use convertCreatedDate as it can return "Unknown"
            sortA = new Date(a?.created)
            sortB = new Date(b?.created)

            break
          }
          case "priority": {
            // Order by priority not alphabetically
            const priorityOrder = {
              urgent: 1,
              high: 2,
              medium: 3,
              low: 4,
            }

            sortA = priorityOrder[a.priority?.toLowerCase()] ?? 5
            sortB = priorityOrder[b.priority?.toLowerCase()] ?? 5

            break
          }
          default: {
            // Default sorting
            sortA = a[sortBy]
            sortB = b[sortBy]

            break
          }
        }

        if (sortA < sortB) {
          return -1
        }

        if (sortA > sortB) {
          return 1
        }

        return 0
      })

      // Reverse order if header clicked again
      if (sortDescending) {
        items.reverse()
      }

      return items
    },

    addNewCustomAdditionalMessageGroup() {
      if (this.stop_settings.custom_additional_message_for_route) {
        const lastItem =
          this.stop_settings.custom_additional_message_for_route[
            this.stop_settings.custom_additional_message_for_route.length - 1
          ]

        if (lastItem && !lastItem.route_ids.length) {
          store.commit("SET_SNACKBAR", {
            text: "Please select at least one route for the previous group before adding a new group",
            color: "error",
          })

          return
        }
      } else {
        this.stop_settings.custom_additional_message_for_route = []
      }

      this.stop_settings.custom_additional_message_for_route.push({
        route_ids: [],
        custom_additional_message: "",
        custom_additional_message_tts: "",
        message_enabled: true,
      })
      this.openedExpansionPanelIndex = this.stop_settings.custom_additional_message_for_route.length - 1
    },
    getRouteTypeIcon(routeType) {
      const routeTypePresentation = this.getRouteTypePresentation(routeType)

      return routeTypePresentation ? routeTypePresentation.icon : ""
    },
    getRouteTypeLabel(routeType) {
      const routeTypePresentation = this.getRouteTypePresentation(routeType)

      return routeTypePresentation ? routeTypePresentation.text : ""
    },
    addRouteToGroup(i) {
      const group = this.stop_settings.custom_additional_message_for_route[i]
      const routes = this.tempGroupModel[i].map(route => route.route_id)
      group.route_ids = [...group.route_ids, ...routes]
      this.tempGroupModel[i] = []
      this.forceRefreshKey += 1
      store.commit("SET_SNACKBAR", {
        text: "Route(s) added to the group, don't forget to save your changes",
        color: "success",
      })
    },
    removeRouteFromGroup(i, route_id) {
      const group = this.stop_settings.custom_additional_message_for_route[i]
      const index = group.route_ids.indexOf(route_id)
      if (index > -1) {
        group.route_ids.splice(index, 1)
      }
      this.forceRefreshKey += 1
      store.commit("SET_SNACKBAR", {
        text: "Route removed from the group, don't forget to save your changes",
        color: "success",
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.no-text-transform {
  text-transform: unset !important;
}
.v-alert {
  margin-bottom: 0 !important;
}
</style>
